import type { FC } from "react";

import { ChartTooltipContent, type ChartConfig } from "@/components/flexkit/charts";
import { BaseAreaChart } from "@/components/flexkit/charts/BaseAreaChart";
import { formatCompactNumber } from "@/components/flexkit/charts/chart-formatters";
import { ChartLoadingState } from "@/components/flexkit/charts/ChartLoadingState";
import { Heading2 } from "@/components/flexkit/typography/Headings";
import { Text } from "@/components/flexkit/typography/Text";
import { Card } from "@/components/podkit/Card";
import { OCUs } from "@/routes/settings/organization/billing/OCU";

export type ConsumptionChartDataPoint = {
    date: string;
    [key: string]: string | number;
};

type Props = {
    title: string;
    dataKey: string;
    data: ConsumptionChartDataPoint[];
    color: string;
    totalOCU?: number;
    daysLabel?: string;
    /** Optional action element rendered at the top-right of the card header. */
    headerAction?: React.ReactNode;
    loading?: boolean;
    /** When true, shows an empty state message inside the chart area instead of the chart. */
    empty?: boolean;
    emptyTitle?: string;
    emptyDescription?: string;
};

export const ConsumptionChart: FC<Props> = ({
    title,
    dataKey,
    data,
    color,
    totalOCU,
    daysLabel = "7 days",
    headerAction,
    loading,
    empty,
    emptyTitle = "No usage data yet",
    emptyDescription = "When your organization starts using Gitpod, daily credit consumption will appear here.",
}) => {
    const chartConfig = {
        [dataKey]: {
            label: title,
            color,
        },
    } satisfies ChartConfig;

    return (
        <Card className="p-6">
            <div className="mb-4 flex items-center justify-between">
                <Text className="text-lg font-semibold text-content-primary">
                    {title}
                    {totalOCU !== undefined && (
                        <span className="ml-2 text-sm font-normal text-content-secondary">
                            ({Math.round(totalOCU).toLocaleString()} <OCUs /> in {daysLabel})
                        </span>
                    )}
                </Text>
                {headerAction}
            </div>

            {loading ? (
                <ChartLoadingState />
            ) : empty ? (
                <div
                    className="flex h-[250px] flex-col items-center justify-center gap-2 text-center"
                    data-testid="no-usage-data"
                >
                    <Heading2 className="text-xl text-content-primary">{emptyTitle}</Heading2>
                    <Text className="max-w-sm text-base text-content-secondary">{emptyDescription}</Text>
                </div>
            ) : (
                <BaseAreaChart
                    data={data}
                    config={chartConfig}
                    height="sm"
                    yAxisFormatter={formatCompactNumber}
                    tooltipContent={
                        <ChartTooltipContent
                            valueFormatter={(value) => `${Math.round(Number(value)).toLocaleString()} OCUs`}
                        />
                    }
                    series={[{ dataKey, color }]}
                />
            )}
        </Card>
    );
};
